import type { Identifier } from "ra-core";

import type {
  ContactConversationMessage,
  ContactConversationPage,
} from "../../types";

/**
 * Flatten cursor pages of a conversation (newest page first) into one list,
 * de-duplicated by row id. The first occurrence wins, so a row that moved
 * between pages while polling keeps the state from the freshest page.
 * Ordering is left to the timeline builder (it sorts by timestamp).
 */
export function mergeConversationPages(
  pages: Array<ContactConversationPage | undefined | null>,
): ContactConversationMessage[] {
  const seen = new Set<string>();
  const merged: ContactConversationMessage[] = [];
  for (const page of pages) {
    for (const row of page?.data ?? []) {
      const key = String(row.id);
      if (seen.has(key)) continue;
      seen.add(key);
      merged.push(row);
    }
  }
  return merged;
}

/** Row shown in the thread immediately after an operator send succeeds,
 *  before the dashboard read-model catches up. */
export function buildOptimisticOperatorMessage({
  contactId,
  whatsappUserId,
  body,
  idempotencyKey,
  senderKey,
  providerMessageId,
  occurredAt = new Date().toISOString(),
}: {
  contactId: Identifier;
  whatsappUserId: string | null | undefined;
  body: string;
  idempotencyKey: string;
  senderKey?: string | null;
  providerMessageId?: string | null;
  occurredAt?: string;
}): ContactConversationMessage {
  return {
    id: idempotencyKey,
    source_id: idempotencyKey,
    contact_id: contactId,
    whatsapp_user_id: whatsappUserId || String(contactId),
    direction: "outbound",
    role: "operator",
    message_type: "text",
    message_kind: "freeform_text",
    text: body,
    body,
    provider_message_id: providerMessageId ?? null,
    provider_status: "accepted",
    occurred_at: occurredAt,
    source: "crm-optimistic",
    source_confidence: "optimistic",
    raw_payload: senderKey ? { sender_key: senderKey } : undefined,
  };
}

const OPTIMISTIC_MATCH_WINDOW_MS = 5 * 60_000;
const OPTIMISTIC_MAX_AGE_MS = 10 * 60_000;

function normalizeText(value: string | null | undefined): string {
  return (value ?? "").replace(/\s+/g, " ").trim().toLowerCase();
}

/**
 * Drop optimistic rows once the server thread carries the real message: an
 * outbound operator row with the same text (or the same provider message id)
 * within a few minutes of the send. Stale optimistic rows are also dropped so
 * a message the read-model never surfaced does not linger forever.
 */
export function reconcileOptimisticMessages(
  pending: ContactConversationMessage[],
  serverRows: ContactConversationMessage[],
  now: number = Date.now(),
): ContactConversationMessage[] {
  if (pending.length === 0) return pending;
  const outbound = serverRows.filter(
    (row) => row.direction === "outbound" && row.role === "operator",
  );
  return pending.filter((candidate) => {
    const sentAt = Date.parse(candidate.occurred_at);
    if (Number.isFinite(sentAt) && now - sentAt > OPTIMISTIC_MAX_AGE_MS) {
      return false;
    }
    const text = normalizeText(candidate.text ?? candidate.body);
    const matched = outbound.some((row) => {
      if (String(row.id) === String(candidate.id)) return true;
      if (
        candidate.provider_message_id &&
        row.provider_message_id === candidate.provider_message_id
      ) {
        return true;
      }
      if (normalizeText(row.text ?? row.body) !== text) return false;
      const rowAt = Date.parse(row.occurred_at);
      if (!Number.isFinite(rowAt) || !Number.isFinite(sentAt)) return true;
      return Math.abs(rowAt - sentAt) <= OPTIMISTIC_MATCH_WINDOW_MS;
    });
    return !matched;
  });
}
